// Copyright 2024 Sorama B.V.
import { bindable, computedFrom, customElement } from "aurelia-framework";

/** Allows resizing two split pane elements simultaneously, with a draggable line in between */
@customElement("splitter")
export class Splitter {
    // Size of the line thingy that you can drag
    private draggerSize = 10;

    // References of html elements
    protected splitterContainer: HTMLElement;
    protected primaryPart: HTMLElement;
    protected primarySlot: HTMLElement;
    protected secondaryPart: HTMLElement;
    protected secondarySlot: HTMLElement;

    /** Wait to resize the primary slot until the drag is completed*/
    @bindable
    public debouncePrimary = false;
    /** Wait to resize the secondary slot until the drag is completed*/
    @bindable
    public debounceSecondary = false;
    /** Ratio between primary and secondary slot size. Ratio 0 means only secondary and 1 means only primary*/
    @bindable
    public ratio = 0.5;
    /** Whether the splitter should split horizontally or vertically (vertical is like flex-direction: column)*/
    @bindable
    public direction: "horizontal" | "vertical" = "vertical";

    private isDragging = false;

    @computedFrom("direction")
    private get directionSizeName(): "height" | "width" {
        return this.direction === "vertical" ? "height" : "width";
    }
    private get directionClientSizePropertyName(): "clientHeight" | "clientWidth" {
        return this.direction === "vertical" ? "clientHeight" : "clientWidth";
    }

    public attached() {
        // Set the sizes of the 2 splitter parts according to the current bound ratio value
        this.ratioChanged();
    }

    private ratioChanged() {
        if (!this.splitterContainer || !this.primaryPart || !this.secondaryPart) return;

        // The amount of space to divide the parts over
        const totalSize = this.splitterContainer[this.directionClientSizePropertyName] - this.draggerSize;

        this.ratio = Math.min(1, Math.max(0, this.ratio));
        this.primaryPart.style[this.directionSizeName] = `${totalSize * this.ratio}px`;
        this.secondaryPart.style[this.directionSizeName] = `${totalSize * (1 - this.ratio)}px`;
    }

    protected mouseDown(event: MouseEvent) {
        this.isDragging = true;

        // We need to listen to the mousemove and mouseup events on the entire document while we are dragging
        document.addEventListener("mousemove", this.mouseMove.bind(this));
        document.addEventListener("mouseup", this.mouseUp.bind(this));

        // If debouncing, keep the size static until the mouse is released
        if (this.debouncePrimary) {
            this.primarySlot.style[this.directionSizeName] =
                this.primaryPart[this.directionClientSizePropertyName] + "px";
        }
        if (this.debounceSecondary) {
            this.secondarySlot.style[this.directionSizeName] =
                this.secondaryPart[this.directionClientSizePropertyName] + "px";
        }
    }

    protected mouseMove(event: MouseEvent) {
        if (!this.isDragging) return;

        const totalSize = this.splitterContainer[this.directionClientSizePropertyName] - this.draggerSize;
        const splitterContainerStart =
            this.splitterContainer.getBoundingClientRect()[this.direction === "vertical" ? "top" : "left"];
        const mousePos = this.direction === "vertical" ? event.clientY : event.clientX;
        const offsetFromStart = mousePos - splitterContainerStart;

        this.ratio = offsetFromStart / totalSize;
    }

    protected mouseUp(event: MouseEvent) {
        this.isDragging = false;

        // Remove the event listeners
        document.removeEventListener("mousemove", this.mouseMove.bind(this));
        document.removeEventListener("mouseup", this.mouseUp.bind(this));

        // If debouncing, set the size to 100% (of the primary- and secondaryPart) to allow the content to resize
        if (this.debouncePrimary && this.primarySlot?.style) {
            this.primarySlot.style[this.directionSizeName] = "100%";
        }
        if (this.debounceSecondary && this.secondarySlot?.style) {
            this.secondarySlot.style[this.directionSizeName] = "100%";
        }
    }
}
